import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { logCartUpsellAdded, logCartUpsellDisplayed } from 'features/cart/utils/analytics';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { StyleSheet, View } from 'react-native';

import { Calories } from 'components/price-and-calories';
import { toaster } from 'components/toaster/toaster';
import { Typography } from 'components/ui';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import { useSmartAlertsContext } from 'state/smart-alert';
import { useUIContext } from 'state/ui';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { primitive, spacing } from 'styles/constants';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

import {
  UpsellItemButton,
  UpsellItemCalories,
  UpsellItemDetails,
  UpsellItemName,
  UpsellItemPicture,
  UpsellItemPrice,
} from '../styles';

import { buildOneMenuUpsellCartEntry } from './utils';

interface OneMenuUpsellItemProps {
  isInCart?: boolean;
  item: ILocalizedMenuEntry;
  position: number;
}

export const OneMenuUpsellItem = ({ isInCart, item, position }: OneMenuUpsellItemProps) => {
  const formatCalories = useFormatCalories();
  const { isOrderPriceLoading } = useCartContext();
  const { hydrateMinimalCart } = useMinimalCart();
  const { indexedMenu, indexedOffers, indexedRewards } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();
  const { currentStoreDate } = useSmartAlertsContext();
  const { formatCurrencyForLocale } = useUIContext();
  const hasLoggedDisplayedRef = useRef(false);

  const image = useMemo(
    () =>
      mapImageResourceToSanityImage(item.image?.resource ?? '', item.image?.altText?.locale ?? ''),
    [item.image?.altText?.locale, item.image?.resource]
  );

  const priceInCents = item.price?.[posServiceMode] ?? undefined;
  const price = formatCurrencyForLocale(priceInCents ?? 0);
  const calories = formatCalories(item.nutrition?.calories?.def ?? 0);

  useEffect(() => {
    if (hasLoggedDisplayedRef.current) {
      return;
    }

    hasLoggedDisplayedRef.current = true;
    logCartUpsellDisplayed({ menuEntry: item, priceInCents });
  }, [item, priceInCents]);

  const handleAddToCart = useCallback(async () => {
    if (isOrderPriceLoading) {
      return;
    }

    if (!isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime)) {
      return toaster.negative(t('restaurantUnavailableErrorTitle'));
    }

    hapticImpact({ style: HapticsImpactStyle.Light });

    const entry = buildOneMenuUpsellCartEntry({
      item,
      indexedMenu,
      indexedOffers,
      indexedRewards,
      posServiceMode,
    });

    if (!entry) {
      return;
    }

    const isHydrationSuccessful = await hydrateMinimalCart([entry]);
    if (!isHydrationSuccessful) {
      return toaster.negative(t('addToCartError', { itemName: entry.name }));
    }

    logCartUpsellAdded({
      menuEntry: item,
      priceInCents,
      upsellItemPosition: position,
    });

    return toaster.addToCartToast(
      t('addToCartSuccess', { itemName: entry.name }),
      entry.image,
      !isInCart
    );
  }, [
    currentStoreDate,
    hydrateMinimalCart,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isInCart,
    isOrderPriceLoading,
    isStoreAvailable,
    item,
    posServiceMode,
    position,
    priceInCents,
    serviceMode,
    store,
  ]);

  const addItemLabel = t('addItemLabel', { itemName: item.name?.locale });
  const priceAndCaloriesLabel = `${price}. ${calories}.`;
  const itemLabel = `${addItemLabel}. ${priceAndCaloriesLabel}.`;

  return (
    <UpsellItemButton
      accessibilityLabel={itemLabel}
      accessibilityRole="button"
      borderWidth={0}
      disabled={isOrderPriceLoading}
      onPress={handleAddToCart}
      testID="upsell-add-to-order-btn"
      _web={{ cursor: isOrderPriceLoading ? 'not-allowed' : 'pointer' }}
    >
      <UpsellItemPicture image={image} alt={item.name?.locale ?? ''} />

      <UpsellItemDetails accessibilityLabel={priceAndCaloriesLabel}>
        <UpsellItemName testID={`upsell-name-${item.id}`} accessibilityLevel={3}>
          {item.name?.locale}
        </UpsellItemName>

        <View style={styles.priceAndCalories}>
          <UpsellItemPrice testID={`upsell-price-${item.id}`}>
            <Typography testID={`upsell-price-text-${item.id}`}>{price}</Typography>
          </UpsellItemPrice>
          <Calories>
            <Typography variant="copyOne">•</Typography>
            <UpsellItemCalories testID={`upsell-calories-${item.id}`}>
              {calories}
            </UpsellItemCalories>
          </Calories>
        </View>
      </UpsellItemDetails>

      <Icon
        variant="bkUpsellPlusV2"
        title={itemLabel}
        accessibilityRole="button"
        accessibilityLabel={itemLabel}
        color={isOrderPriceLoading ? primitive.$blackOpacity50 : primitive.$bbqBrown}
        size="30"
        marginLeft="$2"
      />
    </UpsellItemButton>
  );
};

const styles = StyleSheet.create({
  addIcon: {
    marginLeft: spacing.$2,
  },
  priceAndCalories: {
    alignItems: 'center',
    flexDirection: 'row',
  },
});
